import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  ArrowsRightLeftIcon, 
  CalendarIcon, 
  ChartBarIcon,
  UserIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import { 
  Chart as ChartJS, // Importar Chart principal para registrar componentes
  BarElement,       // Elemento de barra para gráficos de tipo Bar
  CategoryScale,    // Escala de categorías para el eje X
  LinearScale,      // Escala lineal para el eje Y
  Title,            // Plugin de título del gráfico
  Tooltip,          // Plugin de tooltip al pasar el mouse
  Legend            // Plugin de leyenda del gráfico
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { dashboardApi, participantApi } from '../services/api';

// Registrar los componentes de Chart.js necesarios usando Chart.register()
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

function Comparison() {
  const navigate = useNavigate();
  const [participant, setParticipant] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState({ session1: null, session2: null });
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  useEffect(() => {
    // Buscar la sesión activa del participante en el localStorage
    const activeParticipantStr = localStorage.getItem('activeParticipant');
    
    // Si la sesión existe, proceder a cargar su listado de sesiones de evaluación
    if (activeParticipantStr) {
      // Parsear la cadena JSON a un objeto JavaScript
      const activeParticipant = JSON.parse(activeParticipantStr);
      // Guardar el participante en el estado local de la página
      setParticipant(activeParticipant);
      // Cargar el listado de sesiones realizadas por este participante desde el backend usando su ID real
      loadSessions(activeParticipant.id);
    } else {
      // Si el participante intenta ingresar sin autenticarse, redirigir a Login
      navigate('/login');
    }
  }, [navigate]);

  const loadSessions = async (participantId) => {
    try {
      setLoading(true);
      const response = await participantApi.getTestSessions(participantId);
      setSessions(response.data);
    } catch (error) {
      toast.error('Error al cargar las sesiones');
      console.error('Sessions load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionSelect = (type, sessionId) => {
    setSelectedSessions({
      ...selectedSessions,
      [type]: sessionId
    });
  };

  const compareSessions = async () => {
    if (!selectedSessions.session1 || !selectedSessions.session2) {
      toast.error('Por favor selecciona dos sesiones para comparar');
      return;
    }

    try {
      setComparisonLoading(true);
      const response = await dashboardApi.compareSessions(
        participant.id,
        selectedSessions.session1,
        selectedSessions.session2
      );
      setComparisonData(response.data);
    } catch (error) {
      toast.error('Error al realizar la comparación');
      console.error('Comparison error:', error);
    } finally {
      setComparisonLoading(false);
    }
  };

  const getMaturityLevelColor = (score) => {
    if (score >= 76) return 'maturity-76-100';
    if (score >= 51) return 'maturity-51-75';
    if (score >= 26) return 'maturity-26-50';
    return 'maturity-1-25';
  };

  const getMaturityLevelText = (score) => {
    if (score >= 76) return 'Optimizado / Mentor';
    if (score >= 51) return 'Competente / Consistente';
    if (score >= 26) return 'En Desarrollo';
    return 'Inicial / Reactivo';
  };

  const comparisonChartData = {
    labels: comparisonData?.maturity?.map(item => item.pillar) || [],
    datasets: [
      {
        label: 'Sesión 1',
        data: comparisonData?.maturity?.map(item => item.score1) || [],
        backgroundColor: 'rgba(99, 102, 241, 0.8)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1
      },
      {
        label: 'Sesión 2',
        data: comparisonData?.maturity?.map(item => item.score2) || [],
        backgroundColor: 'rgba(147, 51, 234, 0.8)',
        borderColor: 'rgb(147, 51, 234)',
        borderWidth: 1
      }
    ]
  };

  const comparisonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Comparación de Madurez entre Sesiones'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Comparación de Sesiones</h1>
        <button
          onClick={() => navigate('/progress')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center"
        >
          Ver Progreso
          <ArrowRightIcon className="h-4 w-4 ml-2" />
        </button>
      </div>

      {/* Session Selection */}
      <div className="bg-white rounded-lg card-shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Seleccionar Sesiones para Comparar</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Session 1 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Sesión 1</h3>
            <select
              value={selectedSessions.session1 || ''}
              onChange={(e) => handleSessionSelect('session1', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleccionar sesión...</option>
              {sessions.map((session) => (
                <option key={`s1-${session.id}`} value={session.id}>
                  {new Date(session.session_date).toLocaleDateString('es-ES')}
                </option>
              ))}
            </select>
          </div>

          {/* Session 2 */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Sesión 2</h3>
            <select
              value={selectedSessions.session2 || ''}
              onChange={(e) => handleSessionSelect('session2', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleccionar sesión...</option>
              {sessions.map((session) => (
                <option key={`s2-${session.id}`} value={session.id}>
                  {new Date(session.session_date).toLocaleDateString('es-ES')}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <button
            onClick={compareSessions}
            disabled={!selectedSessions.session1 || !selectedSessions.session2 || comparisonLoading}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {comparisonLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Comparando...
              </>
            ) : (
              'Comparar Sesiones'
            )}
          </button>
        </div>
      </div>

      {/* Comparison Results */}
      {comparisonData && (
        <div className="space-y-6">
          {/* Session Info */}
          <div className="bg-white rounded-lg card-shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Información de las Sesiones</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Sesión 1</h3>
                <p className="text-sm text-gray-600 mb-2">
                  {new Date(comparisonData.session1.date).toLocaleDateString('es-ES', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Sesión 2</h3>
                <p className="text-sm text-gray-600 mb-2">
                  {new Date(comparisonData.session2.date).toLocaleDateString('es-ES', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* MBTI Comparison */}
          {comparisonData.mbti.type1 && comparisonData.mbti.type2 && (
            <div className="bg-white rounded-lg card-shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-indigo-600" />
                Comparación MBTI
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 1</h4>
                  <p className="text-2xl font-bold text-indigo-600 mb-2">{comparisonData.mbti.type1}</p>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.mbti.score1}%</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${comparisonData.mbti.score1}%` }}
                    ></div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 2</h4>
                  <p className="text-2xl font-bold text-indigo-600 mb-2">{comparisonData.mbti.type2}</p>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.mbti.score2}%</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-indigo-600 h-2 rounded-full"
                      style={{ width: `${comparisonData.mbti.score2}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              {comparisonData.mbti.type1 !== comparisonData.mbti.type2 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Cambio de personalidad detectado:</strong> Tu tipo de personalidad ha evolucionado de {comparisonData.mbti.type1} a {comparisonData.mbti.type2}.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* VIA Comparison */}
          {comparisonData.via.strengths1 && comparisonData.via.strengths2 && (
            <div className="bg-white rounded-lg card-shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-purple-600" />
                Comparación VIA
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 1</h4>
                  <p className="text-sm text-gray-600 mb-2">Fortalezas principales:</p>
                  <ul className="list-disc list-inside text-sm text-gray-900 mb-3">
                    {comparisonData.via.strengths1.slice(0, 3).map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.via.score1}%</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 2</h4>
                  <p className="text-sm text-gray-600 mb-2">Fortalezas principales:</p>
                  <ul className="list-disc list-inside text-sm text-gray-900 mb-3">
                    {comparisonData.via.strengths2.slice(0, 3).map((strength, index) => (
                      <li key={index}>{strength}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.via.score2}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Eneagram Comparison */}
          {comparisonData.enneagram.type1 && comparisonData.enneagram.type2 && (
            <div className="bg-white rounded-lg card-shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CalendarIcon className="h-5 w-5 mr-2 text-green-600" />
                Comparación Eneagrama
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 1</h4>
                  <p className="text-2xl font-bold text-green-600 mb-2">Tipo {comparisonData.enneagram.type1}</p>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.enneagram.score1}%</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Sesión 2</h4>
                  <p className="text-2xl font-bold text-green-600 mb-2">Tipo {comparisonData.enneagram.type2}</p>
                  <p className="text-sm text-gray-600">Madurez: {comparisonData.enneagram.score2}%</p>
                </div>
              </div>
            </div>
          )}

          {/* Maturity Comparison */}
          {comparisonData.maturity && comparisonData.maturity.length > 0 && (
            <div className="bg-white rounded-lg card-shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ArrowsRightLeftIcon className="h-5 w-5 mr-2 text-orange-600" />
                Comparación de Madurez
              </h3>
              
              <div className="chart-container h-64 mb-6">
                <Bar data={comparisonChartData} options={comparisonChartOptions} />
              </div>

              <div className="space-y-4">
                {comparisonData.maturity.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-900">{item.pillar}</h4>
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        item.difference > 0 ? 'bg-green-100 text-green-800' :
                        item.difference < 0 ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.difference > 0 ? '+' : ''}{item.difference}%
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Sesión 1</p>
                        <div className="flex items-center">
                          <div className={`flex-1 bg-gray-200 rounded-full h-2 mr-2 ${getMaturityLevelColor(item.score1)}`}>
                            <div 
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{ width: `${item.score1}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{item.score1}%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{getMaturityLevelText(item.score1)}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Sesión 2</p>
                        <div className="flex items-center">
                          <div className={`flex-1 bg-gray-200 rounded-full h-2 mr-2 ${getMaturityLevelColor(item.score2)}`}>
                            <div 
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{ width: `${item.score2}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{item.score2}%</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{getMaturityLevelText(item.score2)}</p>
                      </div>
                      
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          item.difference > 0 ? 'bg-green-500' :
                          item.difference < 0 ? 'bg-red-500' :
                          'bg-gray-500'
                        }`}></div>
                        <span className="text-sm text-gray-700">
                          {item.difference > 0 ? 'Mejora' : item.difference < 0 ? 'Disminución' : 'Sin cambio'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-6 text-white">
            <h3 className="text-lg font-semibold mb-4">Resumen de la Comparación</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold">{comparisonData.maturity?.filter(item => item.difference > 0).length || 0}</p>
                <p className="text-sm opacity-90">Áreas mejoradas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{comparisonData.maturity?.filter(item => item.difference < 0).length || 0}</p>
                <p className="text-sm opacity-90">Áreas disminuidas</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{comparisonData.maturity?.filter(item => item.difference === 0).length || 0}</p>
                <p className="text-sm opacity-90">Áreas estables</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Comparison;
